/**
 * Login flow, mirroring the API's two-step auth (§11):
 *   credentials → (TOTP if the account has it) + workspace pick → session.
 * The workspace pick is where multi-tenancy surfaces: one login can administer several tenants.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type Workspace } from '../api';
import { Wordmark, Button, Banner } from '../ui';

type Phase =
  | { step: 'creds' }
  | { step: 'pick'; preauth: string; totp: boolean; ws: Workspace[]; platformAdmin: boolean };

export function Login(): JSX.Element {
  const { api, signIn, signInPlatform } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ step: 'creds' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submitCreds(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.login(email.trim(), password);
      if (r.workspaces.length === 0 && !r.platformAdmin) {
        setErr('This account has no workspaces. Ask a platform admin to grant access.');
        return;
      }
      // A site owner with one workspace and no second factor has nothing to choose: go straight in.
      const only = r.workspaces.length === 1 ? r.workspaces[0] : undefined;
      if (only?.role === 'owner_operator' && !r.totpRequired && !r.platformAdmin) {
        signIn(await api.openSession(r.preauth, only.tenantId));
        navigate('/', { replace: true });
        return;
      }
      setTenantId(r.workspaces[0]?.tenantId ?? '');
      setPhase({
        step: 'pick',
        preauth: r.preauth,
        totp: r.totpRequired,
        ws: r.workspaces,
        platformAdmin: r.platformAdmin,
      });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not sign in. Check your connection.');
    } finally {
      setBusy(false);
    }
  }

  async function submitSession(e: React.FormEvent) {
    e.preventDefault();
    if (phase.step !== 'pick') return;
    setBusy(true);
    setErr(null);
    try {
      const s = await api.openSession(
        phase.preauth,
        tenantId,
        phase.totp ? totpCode.trim() : undefined,
      );
      signIn(s);
      navigate('/sites', { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open the session.');
    } finally {
      setBusy(false);
    }
  }

  async function enterPlatform() {
    if (phase.step !== 'pick') return;
    setBusy(true);
    setErr(null);
    try {
      const p = await api.openPlatformSession(
        phase.preauth,
        phase.totp ? totpCode.trim() : undefined,
      );
      signInPlatform(p);
      navigate('/platform', { replace: true });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not open the platform session.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center-screen">
      <div className="panel auth-card">
        <div className="card-accent" />
        <div className="panel-pad">
          <Wordmark />
          <p className="lead muted" style={{ margin: '18px 0 20px' }}>
            {phase.step === 'creds'
              ? 'Sign in to administer your sites.'
              : 'Choose a workspace to manage.'}
          </p>

          {err && <Banner kind="err">{err}</Banner>}

          {phase.step === 'creds' ? (
            <form onSubmit={submitCreds}>
              <div className="field">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="field">
                <label htmlFor="pw">Password</label>
                <input
                  id="pw"
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" busy={busy} style={{ width: '100%' }}>
                Continue
              </Button>
            </form>
          ) : (
            <form onSubmit={submitSession}>
              {phase.totp && (
                <div className="field">
                  <label htmlFor="totp">Authenticator code</label>
                  <input
                    id="totp"
                    className="input mono"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              )}
              {phase.ws.length > 0 && (
                <>
                  <div className="field">
                    <label htmlFor="ws">Workspace</label>
                    <select
                      id="ws"
                      className="select"
                      value={tenantId}
                      onChange={(e) => setTenantId(e.target.value)}
                    >
                      {phase.ws.map((w) => (
                        <option key={w.tenantId} value={w.tenantId}>
                          {w.name} — {w.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button type="submit" busy={busy} style={{ width: '100%' }}>
                    Enter SentiqCommand
                  </Button>
                </>
              )}
              {phase.platformAdmin && (
                <button
                  type="button"
                  className={`btn ${phase.ws.length > 0 ? 'btn-ghost' : 'btn-primary'}`}
                  style={{ width: '100%', marginTop: phase.ws.length > 0 ? 10 : 0 }}
                  disabled={busy}
                  onClick={() => void enterPlatform()}
                >
                  Platform administration →
                </button>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ width: '100%', marginTop: 10 }}
                onClick={() => {
                  setPhase({ step: 'creds' });
                  setErr(null);
                }}
              >
                Back
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
